import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { TopNav } from "@/components/TopNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = { PENDING: "pending", UPLOADING: "uploading", DONE: "done", ERROR: "error" };

export default function AdminGallery() {
  const [queue, setQueue] = useState([]); // {file, status, progress, error, photoId}
  const [uploaded, setUploaded] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/photos?limit=500`);
      setUploaded(r.data?.photos || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (files.length === 0) return;
    setQueue((q) => [
      ...q,
      ...files.map((file) => ({
        file,
        status: STATUS.PENDING,
        progress: 0,
        error: null,
        photoId: null,
        localUrl: URL.createObjectURL(file),
      })),
    ]);
  }, []);

  const uploadOne = useCallback(async (item, idx) => {
    setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: STATUS.UPLOADING, progress: 0 } : it)));
    const form = new FormData();
    form.append("file", item.file);
    try {
      const r = await axios.post(`${API}/photos/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          const pct = ev.total ? Math.round((ev.loaded * 100) / ev.total) : 0;
          setQueue((q) => q.map((it, i) => (i === idx ? { ...it, progress: pct } : it)));
        },
      });
      setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: STATUS.DONE, progress: 100, photoId: r.data.id } : it)));
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      setQueue((q) => q.map((it, i) => (i === idx ? { ...it, status: STATUS.ERROR, error: msg } : it)));
      return false;
    }
  }, []);

  const startUpload = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    // Sequential upload — friendlier to backend + storage rate limits
    const currentQueue = queue;
    for (let i = 0; i < currentQueue.length; i++) {
      if (currentQueue[i].status === STATUS.DONE) continue;
      await uploadOne(currentQueue[i], i);
    }
    setBusy(false);
    refresh();
  }, [busy, queue, uploadOne, refresh]);

  const clearFinished = useCallback(() => {
    setQueue((q) => q.filter((it) => it.status !== STATUS.DONE));
  }, []);

  const removeFromQueue = useCallback((idx) => {
    setQueue((q) => q.filter((_, i) => i !== idx));
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleDelete = useCallback(
    async (photoId) => {
      if (!window.confirm("Delete this photo? Guests will no longer see it.")) return;
      try {
        await axios.delete(`${API}/photos/${photoId}`);
        refresh();
      } catch (e) {
        alert("Delete failed. Try again.");
      }
    },
    [refresh]
  );

  const pendingCount = queue.filter((it) => it.status === STATUS.PENDING || it.status === STATUS.UPLOADING).length;
  const doneCount = queue.filter((it) => it.status === STATUS.DONE).length;
  const errorCount = queue.filter((it) => it.status === STATUS.ERROR).length;

  return (
    <main
      data-testid="admin-gallery-page"
      className="relative bg-[#0a0a0a] text-[#FDFBF7] min-h-[100svh] px-5 md:px-12 pb-20"
    >
      <TopNav />
      <div className="max-w-[1300px] mx-auto pt-28 md:pt-36">
        <header className="border-b border-[#D4AF37]/20 pb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="eyebrow-line">Administration</div>
            <h1
              className="font-serif-display mt-4 leading-none"
              style={{ fontSize: "clamp(36px, 5vw, 56px)" }}
            >
              Gallery <span className="italic text-[#D4AF37]">Uploader</span>
            </h1>
          </div>
          <div className="flex gap-6 text-sm">
            <div>
              <div className="overline">Uploaded</div>
              <div className="font-serif-display text-3xl mt-2" data-testid="admin-gallery-count">
                {total}
              </div>
            </div>
          </div>
        </header>

        {/* Drop zone */}
        <section
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className="mt-10 border-2 border-dashed p-10 md:p-16 text-center transition-colors bg-[#0f0f0f]"
          style={{
            borderColor: dragOver ? "#D4AF37" : "rgba(212,175,55,0.3)",
            background: dragOver ? "rgba(212,175,55,0.05)" : "#0f0f0f",
          }}
          data-testid="drop-zone"
        >
          <div className="overline">Drag &amp; Drop Photos Here</div>
          <p className="mt-4 text-[#A3A3A3]">
            or choose them from your device — you can select many at once (up to 25MB each)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.heic,.heif"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn-gold mt-8"
            data-testid="choose-files-btn"
          >
            Choose Photos
          </button>
        </section>

        {/* Queue */}
        {queue.length > 0 && (
          <section className="mt-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="text-sm text-[#A3A3A3]">
                <span className="text-[#FDFBF7]">{queue.length}</span> files ·{" "}
                <span className="text-[#D4AF37]">{doneCount} uploaded</span>
                {errorCount > 0 && (
                  <>
                    {" "}
                    · <span className="text-red-400">{errorCount} failed</span>
                  </>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={startUpload}
                  disabled={busy || pendingCount === 0}
                  className="btn-gold"
                  data-testid="start-upload-btn"
                >
                  {busy ? `Uploading ${doneCount + 1}/${queue.length}…` : "Start Upload"}
                </button>
                {doneCount > 0 && !busy && (
                  <button
                    type="button"
                    onClick={clearFinished}
                    className="btn-ghost"
                    data-testid="clear-done-btn"
                  >
                    Clear Done
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {queue.map((it, idx) => (
                <div
                  key={`${it.file.name}-${idx}`}
                  className="relative aspect-square overflow-hidden bg-[#111] border border-[#D4AF37]/15"
                >
                  <img
                    src={it.localUrl}
                    alt={it.file.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 text-[10px]">
                    {it.status === STATUS.PENDING && (
                      <span className="text-[#A3A3A3] tracking-wider uppercase">Ready</span>
                    )}
                    {it.status === STATUS.UPLOADING && (
                      <span className="text-[#D4AF37] tracking-wider uppercase">{it.progress}%</span>
                    )}
                    {it.status === STATUS.DONE && (
                      <span className="text-[#D4AF37] tracking-wider uppercase">✓ Done</span>
                    )}
                    {it.status === STATUS.ERROR && (
                      <span className="text-red-400 tracking-wider uppercase">Failed</span>
                    )}
                  </div>
                  {it.status === STATUS.PENDING && (
                    <button
                      type="button"
                      onClick={() => removeFromQueue(idx)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white text-xs hover:bg-red-500"
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Uploaded */}
        <section className="mt-16">
          <div className="border-t border-[#D4AF37]/20 pt-10">
            <div className="flex justify-between items-baseline">
              <h2 className="font-serif-display text-2xl md:text-3xl">
                On the site <span className="text-[#A3A3A3] text-base">({total})</span>
              </h2>
              <button
                onClick={refresh}
                className="btn-ghost"
                data-testid="refresh-btn"
              >
                Refresh
              </button>
            </div>
            {uploaded.length === 0 ? (
              <p className="text-[#A3A3A3] mt-8">No photos yet.</p>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 mt-8" data-testid="uploaded-grid">
                {uploaded.map((p) => (
                  <div
                    key={p.id}
                    className="relative aspect-square overflow-hidden bg-[#111] group"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleDelete(p.id)}
                      data-testid={`delete-photo-${p.id}`}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white text-xs opacity-0 group-hover:opacity-100 hover:bg-red-500 transition-opacity"
                      aria-label="Delete photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
